import { NotificationDTO } from '@/api/DTO/notification/notification.interface';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import { Box, ButtonBase, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
	UNREAD_ACCENT_BAR,
	UNREAD_BACKGROUND,
	UNREAD_BACKGROUND_HOVER,
} from './notificationHelper';

interface NotificationItemProps {
	notification: NotificationDTO;
	onClick: (notification: NotificationDTO) => void;
}

/**
 * One row in the bell popover: subject and issued time only.
 *
 * The bodies of the notification templates run six to eight lines each, so the
 * popover shows just enough to recognise a notification and opens the detail page
 * for the rest. The subject is clamped to two lines to keep every row scannable at
 * the same height.
 */
export default function NotificationItem({
	notification,
	onClick,
}: NotificationItemProps) {
	const { t } = useTranslation();
	const { title, createdAt, isRead } = notification;

	return (
		<ButtonBase
			component='div'
			role='button'
			onClick={() => onClick(notification)}
			sx={{
				display: 'block',
				width: '100%',
				textAlign: 'left',
				px: 2.5,
				py: 1.75,
				borderBottom: '1px solid',
				borderColor: 'common.neutral90',
				backgroundColor: isRead ? 'transparent' : UNREAD_BACKGROUND,
				// same bar as the summary table, so a notification looks identical in
				// the two places a staff member sees it
				boxShadow: isRead ? 'none' : UNREAD_ACCENT_BAR,
				transition: 'background-color 120ms ease',
				'&:hover': {
					backgroundColor: isRead ? '#F6F5F0' : UNREAD_BACKGROUND_HOVER,
				},
				'&:last-of-type': { borderBottom: 'none' },
			}}
		>
			{!isRead && (
				<Box component='span' sx={visuallyHidden}>
					{t('notification.statusUnread')}
				</Box>
			)}
			<Typography
				sx={{
					fontSize: 14,
					lineHeight: 1.45,
					fontWeight: isRead ? 400 : 700,
					color: 'common.neutral20',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
				}}
			>
				{title}
			</Typography>
			<Typography sx={{ mt: 0.5, fontSize: 12, color: 'common.neutral50' }}>
				{dayjs(createdAt).format(DISPLAY_DATE_TIME_FORMAT)}
			</Typography>
		</ButtonBase>
	);
}
